import { useState, useEffect, useCallback } from 'react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

// Define the missing constant
const LOCATION_STORAGE_KEY = 'service_location';

export interface Category {
  id: string;
  name: string;
}

export interface ImagePreview {
  file: File;
  previewUrl: string;
  isExisting?: boolean;
  id?: string;
}

export interface FormData {
  name: string;
  category: string;
  price: string;
  discountedPrice: string;
  duration: string;
  customDuration: string;
  description: string;
  instantBooking: boolean;
  requiresScheduling: boolean;
  serviceMode: string;
  travelFee: string;
  serviceArea: string;
}

interface InitialData {
  id?: string;
  name?: string;
  price?: number;
  description?: string | null;
  is_active?: boolean;
  category_id?: string | null;
  image_url?: string | null;
  service_mode?: string;
  travel_fee?: number | null;
  service_area?: string | null;
  instant_booking?: boolean;
  requires_scheduling?: boolean;
  duration?: number | null;
  duration_unit?: string | null;
  discounted_price?: number | null;
}

// Default initial form values
const getDefaultFormValues = (): FormData => {
  let savedLocation = "";
  try {
    savedLocation = localStorage.getItem(LOCATION_STORAGE_KEY) || "";
  } catch (e) {
    console.error("Error accessing localStorage:", e);
  }

  return {
    name: "",
    category: "",
    price: "",
    discountedPrice: "", 
    duration: "1-hour",
    customDuration: "",
    description: "",
    instantBooking: false,
    requiresScheduling: true, // Default to requiring scheduling
    serviceMode: "my-location",
    travelFee: "",
    serviceArea: savedLocation,
  };
};

export const useServiceForm = (initialData?: InitialData) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(initialData ? true : false);
  const [categories, setCategories] = useState<Category[]>([]);
  const [images, setImages] = useState<ImagePreview[]>([]);
  const [coverImageIndex, setCoverImageIndex] = useState<number>(0);
  const [selectedCategoryName, setSelectedCategoryName] = useState<string>("");
  const [userProfession, setUserProfession] = useState<string | null>(null);
  const [categoriesFetched, setCategoriesFetched] = useState<boolean>(false);
  const [formInitialized, setFormInitialized] = useState<boolean>(false);

  const getSavedLocation = () => {
    try {
      return localStorage.getItem(LOCATION_STORAGE_KEY) || "";
    } catch (e) {
      console.error("Error accessing localStorage:", e);
      return "";
    }
  };

  const getInitialDuration = (duration?: number | null): string => {
    if (!duration) return "1-hour";
    
    if (duration === 30) return "30-min";
    if (duration === 60) return "1-hour";
    if (duration === 120) return "2-hours";
    
    return "custom";
  };

  // Initialize form data based on initial data or defaults
  const getInitialFormData = (data?: InitialData): FormData => {
    if (data) {
      return {
        name: data.name || "",
        category: data.category_id || "",
        price: data.price ? data.price.toString() : "",
        discountedPrice: data.discounted_price ? data.discounted_price.toString() : "", 
        duration: getInitialDuration(data.duration),
        customDuration: data.duration && ![30, 60, 120].includes(data.duration) 
          ? data.duration.toString() 
          : "",
        description: data.description || "",
        instantBooking: data.instant_booking ?? false,
        requiresScheduling: data.requires_scheduling ?? true, // Default to true if not set
        serviceMode: data.service_mode || "my-location",
        travelFee: data.travel_fee ? data.travel_fee.toString() : "",
        serviceArea: data.service_area || getSavedLocation(),
      };
    }
    
    return getDefaultFormValues();
  };

  const [formData, setFormData] = useState<FormData>(getInitialFormData(initialData));

  // Reset form to default values
  const resetForm = useCallback(() => {
    console.log("Resetting service form to defaults");
    setFormData(getDefaultFormValues());
    setImages([]);
    setCoverImageIndex(0);
    setFormInitialized(false);
  }, []);

  // Set or update form data when initialData changes
  useEffect(() => {
    if (initialData) {
      console.log("Initial data changed, updating form data:", initialData);
      setFormData(getInitialFormData(initialData));
      setFormInitialized(true);
    }
  }, [initialData]);

  useEffect(() => {
    fetchUserProfile();
  }, []);

  useEffect(() => {
    if (initialData?.id) {
      console.log("Initial data provided, fetching service details", initialData);
      fetchServiceDetails(initialData.id);
      fetchServiceImages(initialData.id);
    }
  }, [initialData?.id]);

  const fetchUserProfile = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        console.error('No authenticated user found');
        return;
      }

      const { data, error } = await supabase
        .from('profiles')
        .select('profession')
        .eq('id', user.id)
        .single();

      if (error) {
        console.error('Error fetching user profile:', error);
        return;
      }
      
      if (data) {
        setUserProfession(data.profession);
        console.log('User profession:', data.profession);
      }
    } catch (error) {
      console.error('Error in fetchUserProfile:', error);
    }
  };

  const fetchServiceDetails = async (serviceId: string) => {
    try {
      setLoading(true);
      console.log("Fetching service details for ID:", serviceId);
      
      const { data: service, error } = await supabase
        .from('services')
        .select('*')
        .eq('id', serviceId)
        .single();
      
      if (error) {
        console.error('Error fetching service details:', error);
        throw error;
      }
      
      if (service) {
        console.log('Fetched service details:', service);
        
        setFormData({
          name: service.name || "",
          category: service.category_id || "",
          price: service.price ? service.price.toString() : "",
          discountedPrice: service.discounted_price ? service.discounted_price.toString() : "",
          duration: getInitialDuration(service.duration),
          customDuration: service.duration && ![30, 60, 120].includes(service.duration) 
            ? service.duration.toString() 
            : "",
          description: service.description || "",
          instantBooking: service.instant_booking ?? false,
          requiresScheduling: service.requires_scheduling ?? true, // Use the new field
          serviceMode: service.service_mode || "my-location",
          travelFee: service.travel_fee ? service.travel_fee.toString() : "",
          serviceArea: service.service_area || getSavedLocation(),
        });
        setFormInitialized(true);
      } else {
        console.error("No service found with ID:", serviceId);
        toast({
          title: "Error",
          description: "Service not found",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Error in fetchServiceDetails:', error);
      toast({
        title: "Error",
        description: "Failed to load service details",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchServiceImages = async (serviceId: string) => {
    try {
      console.log("Fetching images for service ID:", serviceId);
      const { data: service, error: serviceError } = await supabase
        .from('services')
        .select('image_url')
        .eq('id', serviceId)
        .single();
      
      if (serviceError) {
        console.error('Error fetching service:', serviceError);
        return;
      }
      
      const { data: serviceImages, error: imagesError } = await supabase
        .from('service_images')
        .select('id, image_url, sequence')
        .eq('service_id', serviceId)
        .order('sequence', { ascending: true });
      
      if (imagesError) {
        console.error('Error fetching service images:', imagesError);
        return;
      }
      
      console.log("Service cover image:", service?.image_url);
      console.log("Additional service images:", serviceImages);
      
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      console.log("Using Supabase URL:", supabaseUrl);
      
      const allImages: { url: string, sequence: number, id?: string }[] = [];
      
      if (service?.image_url) {
        // Extract the path from the full URL if it exists, or use the URL as is
        const imagePath = service.image_url.includes('/storage/v1/object/public/') 
          ? service.image_url 
          : `${supabaseUrl}/storage/v1/object/public/service_images/${service.image_url}`;
        
        console.log('Found service cover image:', imagePath);
        allImages.push({
          url: imagePath,
          sequence: -1,  // Special sequence for cover image
          // We don't have id for the cover image if it's stored on the service itself
        });
      }
      
      if (serviceImages && serviceImages.length > 0) {
        console.log('Found additional service images:', serviceImages.length);
        serviceImages.forEach(img => {
          // Extract the path from the full URL if it exists, or use the URL as is
          const imagePath = img.image_url.includes('/storage/v1/object/public/') 
            ? img.image_url 
            : `${supabaseUrl}/storage/v1/object/public/service_images/${img.image_url}`;
            
          if (imagePath !== service?.image_url) {
            allImages.push({
              url: imagePath,
              sequence: img.sequence,
              id: img.id // Include the image ID from the database
            });
          }
        });
      }
      
      console.log('Total images to display:', allImages.length);
      
      // Update the mapping to include the id in the image preview objects
      const imagePreviewsArray = allImages.map((img) => {
        return {
          file: null as any,
          previewUrl: img.url,
          isExisting: true,
          id: img.id // Add the ID to the image preview
        };
      });
      
      setImages(imagePreviewsArray);
      
      if (imagePreviewsArray.length > 0) {
        const coverIndex = allImages.findIndex(img => img.sequence === -1);
        if (coverIndex >= 0) {
          setCoverImageIndex(coverIndex);
        } else {
          setCoverImageIndex(0); // Default to first image if no cover specified
        }
      }
      
    } catch (error) {
      console.error('Error in fetchServiceImages:', error);
      toast({
        title: "Error",
        description: "Failed to load service images",
        variant: "destructive",
      });
    }
  };

  const fetchCategories = useCallback(async () => {
    // Prevent multiple category fetches
    if (categoriesFetched) {
      console.log("Categories already fetched, skipping redundant fetch");
      return;
    }
    
    try {
      console.log("Fetching service categories");
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        console.error('No authenticated user found');
        toast({
          title: "Error",
          description: "Authentication error. Please login again.",
          variant: "destructive",
        });
        return;
      }

      const { data, error } = await supabase
        .from('service_categories')
        .select('*')
        .eq('user_id', user.id)
        .eq('is_visible', true)
        .order('sequence', { ascending: true });

      if (error) {
        console.error('Error fetching categories:', error);
        toast({
          title: "Error",
          description: "Failed to load categories",
          variant: "destructive",
        });
      } else {
        console.log('Fetched categories:', data);
        setCategories(data || []);
        setCategoriesFetched(true);
        
        // If we have a selected category, check if it still exists
        if (formData.category) {
          const categoryExists = data?.some(cat => cat.id === formData.category);
          if (!categoryExists && data && data.length > 0) {
            // If the selected category no longer exists, select the first available one
            // without resetting other form fields
            updateFormField('category', data[0].id);
          }
        } else if (data && data.length > 0 && !formInitialized) {
          // Auto-select first category for new services only if form isn't initialized
          // and make sure we don't reset the form
          const newFormData = { ...formData, category: data[0].id };
          setFormData(newFormData);
          setFormInitialized(true);
        }
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      toast({
        title: "Error",
        description: "Failed to load categories",
        variant: "destructive",
      });
    }
  }, [formData, formInitialized, toast, categoriesFetched]);

  useEffect(() => {
    if (formData.category && categories.length > 0) {
      const category = categories.find(cat => cat.id === formData.category);
      setSelectedCategoryName(category?.name || "");
    } else {
      setSelectedCategoryName("");
    }
  }, [formData.category, categories]);

  const handleLocationChange = (location: string) => {
    updateFormField('serviceArea', location);
    try {
      localStorage.setItem(LOCATION_STORAGE_KEY, location);
    } catch (e) {
      console.error("Error storing location in localStorage:", e);
    }
  };

  const updateFormField = <K extends keyof FormData>(field: K, value: FormData[K]) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };
  
  return {
    loading,
    setLoading,
    categories,
    setCategories,
    images, 
    setImages,
    coverImageIndex,
    setCoverImageIndex,
    selectedCategoryName,
    formData,
    updateFormField,
    handleLocationChange,
    userProfession,
    resetForm,
    fetchCategories,
    setFormData,
    formInitialized
  };
};
